import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import api, { ApiError } from './api/client';
import Ornament from './components/Ornament';
import Icon from './components/Icon';
import './Signin.css';

// Allow only same-origin paths for the next param to prevent open-redirects.
const safeNext = (raw, fallback = '/') => {
    if (!raw) return fallback;
    if (!raw.startsWith('/') || raw.startsWith('//')) return fallback;
    return raw;
};

const SignIn = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const nextPath = safeNext(new URLSearchParams(location.search).get('next'));

    const handleSignIn = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setError('');
        setIsSubmitting(true);
        try {
            const data = await api.post('/api/auth/signin', { email, password });
            localStorage.setItem('token', data.token);
            localStorage.setItem('role', data.user.role);
            localStorage.setItem('userId', data.user.id);
            localStorage.setItem('userName', data.user.name);
            localStorage.setItem('email', data.user.email);
            window.dispatchEvent(new Event('storage'));
            // Honour ?next=… first; otherwise route by role.
            if (nextPath !== '/') {
                navigate(nextPath);
            } else {
                navigate(data.user.role === 'admin' ? '/admin-dashboard' : '/');
            }
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Unable to reach the server. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="auth-page">
            <div className="auth-card">
                <div className="auth-card-header">
                    <span className="swadharma-seal auth-seal" aria-hidden="true">ॐ</span>
                    <Ornament variant="rule" tone="gold" size={140} className="auth-rule" />
                    <h1>Welcome back</h1>
                    <p className="sw-text-muted">Sign in to continue to Samudyoga</p>
                </div>

                <form className="auth-form" onSubmit={handleSignIn}>
                    <div className="auth-field">
                        <label htmlFor="email">Email address</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            required
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="email"
                        />
                    </div>

                    <div className="auth-field">
                        <div className="auth-field-label-row">
                            <label htmlFor="password">Password</label>
                            <a href="#" className="auth-link-muted" onClick={(e) => e.preventDefault()}>
                                Forgot password?
                            </a>
                        </div>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            required
                            minLength={8}
                            placeholder="At least 8 characters"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                        />
                    </div>

                    {error && <div className="auth-error" role="alert">{error}</div>}

                    <button
                        type="submit"
                        className="sw-btn sw-btn-saffron sw-btn-lg auth-submit"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Signing in…' : 'Sign in'}
                    </button>

                    <p className="auth-alt">
                        Don't have an account? <Link to="/signup">Register</Link>
                    </p>
                </form>
            </div>

            <aside className="auth-aside">
                <img
                    src="/samudyoga-logo.png"
                    alt="Samudyoga · A Dharma Poshanam initiative"
                    className="auth-aside-logo"
                />
                <div className="auth-aside-inner">
                    <span className="auth-aside-eyebrow">Connect · Engage · Empower</span>
                    <h2>Samudyoga<br />Job Portal under Swadharma Project</h2>
                    <ul className="auth-aside-list">
                        <li><Icon name="check" size={16} /> Connect Dharmikas with professionals &amp; service providers</li>
                        <li><Icon name="check" size={16} /> One-event, short-term and long-term engagements</li>
                        <li><Icon name="check" size={16} /> Verified profiles · privacy-first</li>
                    </ul>
                    <span className="auth-aside-tagline">
                        A Dharma Poshanam initiative · under Swadharma
                    </span>
                </div>
            </aside>
        </div>
    );
};

export default SignIn;
